import { ApiResponse } from './common';

// ========== Feature Types ==========

export type FeatureDepartment = 'OMS' | 'AMS' | 'EPC' | 'COMMON';

/** For "common to selected business units" (e.g. only OMS + AMS). No COMMON. */
export type MultiDeptValue = 'OMS' | 'AMS' | 'EPC';

/** Data scope: shared = one API, data visible across BUs; bu-scoped = data isolated per BU. */
export type FeatureDataScope = 'shared' | 'bu-scoped';

export const FEATURE_SCOPE_CONFIG: Record<string, { defaultBU: FeatureDepartment; dataScope: FeatureDataScope }> = {
  // COMMON + shared data
  userManagement:   { defaultBU: 'COMMON', dataScope: 'shared' },
  roleManagement:   { defaultBU: 'COMMON', dataScope: 'shared' },
  dashboard:        { defaultBU: 'COMMON', dataScope: 'shared' },
  report:           { defaultBU: 'COMMON', dataScope: 'shared' },
  // Shared-data masters (any BU creates, all BUs see)
  projectType:      { defaultBU: 'COMMON', dataScope: 'shared' },
  assetMaster:      { defaultBU: 'COMMON', dataScope: 'shared' },
  customer:         { defaultBU: 'COMMON', dataScope: 'shared' },
  customerGroup:    { defaultBU: 'COMMON', dataScope: 'shared' },
  location:         { defaultBU: 'COMMON', dataScope: 'shared' },
  uomMaster:        { defaultBU: 'COMMON', dataScope: 'shared' },
  documentMaster:   { defaultBU: 'COMMON', dataScope: 'shared' },
  manufacturer:     { defaultBU: 'COMMON', dataScope: 'shared' },
  // OMS BU-scoped
  projectMaster:    { defaultBU: 'OMS',    dataScope: 'bu-scoped' },
  taskMaster:       { defaultBU: 'OMS',    dataScope: 'bu-scoped' },
  taskExecution:    { defaultBU: 'OMS',    dataScope: 'bu-scoped' },
  checkpointMaster: { defaultBU: 'OMS',   dataScope: 'bu-scoped' },
  equipmentMaster:  { defaultBU: 'OMS',    dataScope: 'bu-scoped' },
  siteSupervisor:   { defaultBU: 'OMS',    dataScope: 'bu-scoped' },
  // AMS BU-scoped
  assetManagement:  { defaultBU: 'AMS',    dataScope: 'bu-scoped' },
  maintenance:      { defaultBU: 'AMS',    dataScope: 'bu-scoped' },
  // EPC BU-scoped
  procurement:      { defaultBU: 'EPC',    dataScope: 'bu-scoped' },
  engineering:      { defaultBU: 'EPC',    dataScope: 'bu-scoped' },
  epcProject:       { defaultBU: 'EPC',    dataScope: 'bu-scoped' },
};

export function getModuleScope(permissionKey: string): { defaultBU: FeatureDepartment; dataScope: FeatureDataScope } | undefined {
  const module = permissionKey.split('.')[0];
  return FEATURE_SCOPE_CONFIG[module];
}

export function isSharedDataModule(permissionKey: string): boolean {
  return getModuleScope(permissionKey)?.dataScope === 'shared';
}

export interface Feature {
  _id: string;
  key: string;
  name: string;
  route: string;
  icon: string;
  permissionKey: string;
  parentKey?: string;
  businessUnit: FeatureDepartment;
  /** When set: feature is visible in exactly these business units (e.g. [OMS, AMS]). */
  businessUnits?: MultiDeptValue[];
  /** DB source of truth: list of business unit IDs that can see this feature. COMMON = all 3, two units = 2 IDs. */
  businessUnitIds?: MultiDeptValue[];
  /** @deprecated Use businessUnit */
  department?: FeatureDepartment;
  /** @deprecated Use businessUnits */
  departments?: MultiDeptValue[];
  /** @deprecated Use businessUnitIds */
  departmentIds?: MultiDeptValue[];
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /** Optional; derived from permissionKey via FEATURE_SCOPE_CONFIG if absent. */
  dataScope?: FeatureDataScope;
  /** Per RBAC overhaul spec §4.1 — tooltip / help text for the permission matrix. */
  description?: string;
  /**
   * Per RBAC overhaul spec §4.1 — list of action keys this feature supports
   * (e.g. `['read', 'create', 'update', 'delete']`).
   */
  actions?: string[];
}

// ========== Filter Types ==========

export interface FeatureFilter {
  search?: string;
  isActive?: boolean;
  parentKey?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  businessUnit?: FeatureDepartment | 'ALL';
  dataScope?: FeatureDataScope | 'ALL';
}

// ========== Form Types ==========

export interface CreateFeatureForm {
  key: string;
  name: string;
  route: string;
  icon: string;
  permissionKey: string;
  parentKey?: string;
  businessUnit: FeatureDepartment;
  /** For "common to selected business units" only (e.g. [OMS, AMS]). */
  businessUnits?: MultiDeptValue[];
  order: number;
  isActive: boolean;
  /**
   * Per RBAC overhaul spec §4.1 — Tooltip / help text rendered in the
   * permission matrix and (optionally) the menu sidebar. Optional; backend
   * defaults to `''`.
   */
  description?: string;
  /**
   * Per RBAC overhaul spec §4.1 — list of action keys this feature supports
   * (e.g. `['read', 'create', 'update', 'delete']`). Drives the columns
   * available for this module in the role permission matrix. Optional;
   * backend defaults to `['read']`.
   */
  actions?: string[];
}

export interface UpdateFeatureForm extends Partial<CreateFeatureForm> {
  _id: string;
}

// ========== API Response Types ==========

export interface FeatureListResponse {
  docs: Feature[];
  totalDocs: number;
  limit: number;
  page: number;
  totalPages: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
  prevPage: number | null;
  nextPage: number | null;
}

export interface FeatureResponse extends ApiResponse<Feature> {}

// ========== Business Unit View Types ==========

export interface BusinessUnitFeatureStats {
  businessUnit: string;
  label: string;
  count: number;
  parentCount: number;
  childCount: number;
  activeCount: number;
  inactiveCount: number;
  features: Feature[];
}

export interface BusinessUnitViewResult {
  common: BusinessUnitFeatureStats;
  businessUnits: BusinessUnitFeatureStats[];
  total: number;
}

export interface BusinessUnitViewResponse extends ApiResponse<BusinessUnitViewResult> {}

/** @deprecated Use BusinessUnitFeatureStats */
export type DepartmentFeatureStats = BusinessUnitFeatureStats;
/** @deprecated Use BusinessUnitViewResult */
export type DepartmentViewResult = BusinessUnitViewResult;

export type DeptFilter = 'ALL' | 'COMMON' | 'OMS' | 'AMS' | 'EPC';

// ========== Icon Options ==========

export const ICON_OPTIONS = [
  { value: 'GridIcon', label: 'Dashboard', icon: 'GridIcon' },
  { value: 'UserCircleIcon', label: 'User Management', icon: 'UserCircleIcon' },
  { value: 'SettingsIcon', label: 'Settings', icon: 'SettingsIcon' },
  { value: 'DatabaseIcon', label: 'Database', icon: 'DatabaseIcon' },
  { value: 'BoxIcon', label: 'Assets', icon: 'BoxIcon' },
  { value: 'FolderIcon', label: 'Projects', icon: 'FolderIcon' },
  { value: 'TaskIcon', label: 'Tasks', icon: 'TaskIcon' },
  { value: 'ListIcon', label: 'Lists', icon: 'ListIcon' },
  { value: 'PieChartIcon', label: 'Reports', icon: 'PieChartIcon' },
  { value: 'FileTextIcon', label: 'Documents', icon: 'FileTextIcon' },
  { value: 'TableIcon', label: 'Tables', icon: 'TableIcon' },
  { value: 'HomeIcon', label: 'Home', icon: 'HomeIcon' },
  { value: 'PlusIcon', label: 'Add', icon: 'PlusIcon' },
  { value: 'SearchIcon', label: 'Search', icon: 'SearchIcon' },
  { value: 'FilterIcon', label: 'Filter', icon: 'FilterIcon' },
  { value: 'EyeIcon', label: 'View', icon: 'EyeIcon' },
  { value: 'PencilIcon', label: 'Edit', icon: 'PencilIcon' },
  { value: 'TrashIcon', label: 'Delete', icon: 'TrashIcon' },
  { value: 'UploadIcon', label: 'Upload', icon: 'UploadIcon' },
  { value: 'DownloadIcon', label: 'Download', icon: 'DownloadIcon' },
  { value: 'CheckCircleIcon', label: 'Success', icon: 'CheckCircleIcon' },
  { value: 'AlertIcon', label: 'Alert', icon: 'AlertIcon' },
  { value: 'InfoIcon', label: 'Info', icon: 'InfoIcon' },
  { value: 'ErrorIcon', label: 'Error', icon: 'ErrorIcon' },
  { value: 'BoltIcon', label: 'Power', icon: 'BoltIcon' },
  { value: 'ArrowUpIcon', label: 'Up', icon: 'ArrowUpIcon' },
  { value: 'ArrowDownIcon', label: 'Down', icon: 'ArrowDownIcon' },
  { value: 'ArrowRightIcon', label: 'Right', icon: 'ArrowRightIcon' },
  { value: 'ArrowLeftIcon', label: 'Left', icon: 'ArrowLeftIcon' },
  { value: 'ChevronUpIcon', label: 'Chevron Up', icon: 'ChevronUpIcon' },
  { value: 'ChevronDownIcon', label: 'Chevron Down', icon: 'ChevronDownIcon' },
  { value: 'ChevronLeftIcon', label: 'Chevron Left', icon: 'ChevronLeftIcon' },
  { value: 'ChevronRightIcon', label: 'Chevron Right', icon: 'ChevronRightIcon' },
  { value: 'AngleUpIcon', label: 'Angle Up', icon: 'AngleUpIcon' },
  { value: 'AngleDownIcon', label: 'Angle Down', icon: 'AngleDownIcon' },
  { value: 'AngleLeftIcon', label: 'Angle Left', icon: 'AngleLeftIcon' },
  { value: 'AngleRightIcon', label: 'Angle Right', icon: 'AngleRightIcon' },
  { value: 'CloseIcon', label: 'Close', icon: 'CloseIcon' },
  { value: 'CheckLineIcon', label: 'Check', icon: 'CheckLineIcon' },
  { value: 'CloseLineIcon', label: 'Close Line', icon: 'CloseLineIcon' },
  { value: 'PaperPlaneIcon', label: 'Send', icon: 'PaperPlaneIcon' },
  { value: 'LockIcon', label: 'Security', icon: 'LockIcon' },
  { value: 'EnvelopeIcon', label: 'Email', icon: 'EnvelopeIcon' },
  { value: 'UserIcon', label: 'User', icon: 'UserIcon' },
  { value: 'CalenderIcon', label: 'Calendar', icon: 'CalenderIcon' },
  { value: 'EyeIcon', label: 'View', icon: 'EyeIcon' },
  { value: 'EyeCloseIcon', label: 'Hide', icon: 'EyeCloseIcon' },
  { value: 'TimeIcon', label: 'Time', icon: 'TimeIcon' },
  { value: 'CopyIcon', label: 'Copy', icon: 'CopyIcon' },
  { value: 'GroupIcon', label: 'Group', icon: 'GroupIcon' },
  { value: 'ShootingStarIcon', label: 'Star', icon: 'ShootingStarIcon' },
  { value: 'DollarLineIcon', label: 'Money', icon: 'DollarLineIcon' },
  { value: 'TrashBinIcon', label: 'Trash', icon: 'TrashBinIcon' },
  { value: 'PlugInIcon', label: 'Plugin', icon: 'PlugInIcon' },
  { value: 'DocsIcon', label: 'Documentation', icon: 'DocsIcon' },
  { value: 'MailIcon', label: 'Mail', icon: 'MailIcon' },
  { value: 'ChatIcon', label: 'Chat', icon: 'ChatIcon' },
  { value: 'MoreDotIcon', label: 'More', icon: 'MoreDotIcon' },
  { value: 'AlertHexaIcon', label: 'Alert Hexagon', icon: 'AlertHexaIcon' },
  { value: 'ErrorHexaIcon', label: 'Error Hexagon', icon: 'ErrorHexaIcon' },
];

// All 8 valid actions
export const ALL_PERMISSION_ACTIONS = [
  'read', 'create', 'update', 'delete', 'activate', 'deactivate', 'generate', 'manage', 'import'
] as const;
export type PermissionAction = typeof ALL_PERMISSION_ACTIONS[number];

// All known modules with their departments
export const KNOWN_PERMISSION_MODULES = [
  // COMMON
  { key: 'userManagement',   label: 'User Management',   dept: 'COMMON' as FeatureDepartment },
  { key: 'roleManagement',   label: 'Role Management',   dept: 'COMMON' as FeatureDepartment },
  { key: 'dashboard',        label: 'Dashboard',         dept: 'COMMON' as FeatureDepartment },
  { key: 'report',           label: 'Reports',           dept: 'COMMON' as FeatureDepartment },
  // OMS
  { key: 'projectType',      label: 'Project Type',      dept: 'OMS' as FeatureDepartment },
  { key: 'projectMaster',    label: 'Project Master',    dept: 'OMS' as FeatureDepartment },
  { key: 'assetMaster',      label: 'Asset Master',      dept: 'OMS' as FeatureDepartment },
  { key: 'taskMaster',       label: 'Task Master',       dept: 'OMS' as FeatureDepartment },
  { key: 'taskExecution',    label: 'Task Execution',    dept: 'OMS' as FeatureDepartment },
  { key: 'checkpointMaster', label: 'Checkpoint Master', dept: 'OMS' as FeatureDepartment },
  { key: 'equipmentMaster',  label: 'Equipment Master',  dept: 'OMS' as FeatureDepartment },
  { key: 'siteSupervisor',   label: 'Site Supervisor',   dept: 'OMS' as FeatureDepartment },
  // AMS
  { key: 'assetManagement',  label: 'Asset Management',  dept: 'AMS' as FeatureDepartment },
  { key: 'maintenance',      label: 'Maintenance',       dept: 'AMS' as FeatureDepartment },
  // EPC
  { key: 'procurement',      label: 'Procurement',       dept: 'EPC' as FeatureDepartment },
  { key: 'engineering',      label: 'Engineering',       dept: 'EPC' as FeatureDepartment },
  { key: 'epcProject',       label: 'EPC Projects',      dept: 'EPC' as FeatureDepartment },
];

// ========== Permission Key Options (full — all modules × all actions) ==========

export const PERMISSION_KEY_OPTIONS: { value: string; label: string; module: string; action: string; dept: string }[] =
  KNOWN_PERMISSION_MODULES.flatMap(m =>
    ALL_PERMISSION_ACTIONS.map(action => ({
      value: `${m.key}.${action}`,
      label: `${m.label} — ${action.charAt(0).toUpperCase() + action.slice(1)}`,
      module: m.key,
      action,
      dept: m.dept,
    }))
  ); 